import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// Open a server-sent-events channel against /api/stream while signed in.
// The backend publishes a "refresh" signal whenever the webhook sync
// finishes for one of this user's calendars; we invalidate the relevant
// react-query keys so the UI refetches immediately instead of waiting for
// the 60-second poll.
//
// We also invalidate on (re)connect — EventSource auto-reconnects with
// exponential backoff if the network blips, and any events published while
// we were disconnected were dropped. Invalidating on reconnect catches us
// up via a normal refetch.
//
// `enabled` mirrors the auth state; flipping it off (sign-out) tears down
// the connection cleanly.
export function useEventStream(enabled: boolean): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;

    const es = new EventSource('/api/stream', { withCredentials: true });

    const invalidate = () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['agenda'] });
      qc.invalidateQueries({ queryKey: ['countdowns'] });
      qc.invalidateQueries({ queryKey: ['calendars'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['preferences'] });
      qc.invalidateQueries({ queryKey: ['weather'] });
      qc.invalidateQueries({ queryKey: ['weather-prefs'] });
      qc.invalidateQueries({ queryKey: ['plan'] });
      qc.invalidateQueries({ queryKey: ['plan-rhythms'] });
      qc.invalidateQueries({ queryKey: ['plan-tasks'] });
    };

    es.addEventListener('hello', invalidate);
    es.addEventListener('message', invalidate);
    es.onerror = () => {
      // EventSource handles reconnect itself; on a transient failure the
      // browser will reopen and the next 'hello' will invalidate again.
      // Nothing to do here beyond letting it retry.
    };

    return () => {
      es.close();
    };
  }, [enabled, qc]);
}
